import { createItem, updateItem } from '@directus/sdk';
import { keepPreviousData, useMutation, useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';
import type { DeepPartial } from 'react-hook-form';

import { useIncompleteEventsItemQuery } from '../../../data/incompleteEvents/useIncompleteEventsItemQuery.ts';
import { useIncompleteEventsItemUpdateMutation } from '../../../data/incompleteEvents/useIncompleteEventsItemUpdateMutation.ts';
import { usePromoterItemQuery } from '../../../data/promoter/usePromoterItemQuery.ts';
import type { IncompleteEventFormComplete } from '../../../forms/event/IncompleteEventForm/schema.ts';
import { directusClient } from '../../../services/directusClient/directusClient.ts';
import { tanstackQueryClient } from '../../../services/tanstackQueryClient.ts';
import { incompleteEventApiToIncompleteEventEditForm } from '../../incompleteEventEdit/lib/incompleteEventApiToIncompleteEventEditForm.ts';
import { incompleteEventEditFormToIncompleteEventApi } from '../../incompleteEventEdit/lib/incompleteEventEditFormToIncompleteEventApi.ts';

/**
 * Creates an incomplete event and links it to the promoter.
 * Used as a guard when onboarding_event is missing (e.g. legacy accounts).
 * Module-level set prevents duplicate creation across remounts and strict mode.
 */
const pendingCreations = new Set<number>();

const useCreateMissingOnboardingEvent = (promoter?: { id: number; onboarding_event: string | null } | null) => {
  const { mutate } = useMutation({
    mutationFn: async (promoterId: number) => {
      const result = await directusClient.request(
        createItem('incomplete_events', { name: '', promoter: promoterId as unknown as string }),
      );

      await directusClient.request(updateItem('promoters', promoterId, { onboarding_event: result.id }));
    },
    onSuccess: async () => {
      await tanstackQueryClient.invalidateQueries({ queryKey: ['promoter'] });
    },
    onSettled: () => {
      if (promoter) pendingCreations.delete(promoter.id);
    },
  });

  useEffect(() => {
    if (promoter && !promoter.onboarding_event && !pendingCreations.has(promoter.id)) {
      pendingCreations.add(promoter.id);
      mutate(promoter.id);
    }
  }, [promoter, mutate]);
};

export const useOnboardingIncompleteEvent = (config?: { onMutationSuccess?: () => void }) => {
  const { data: promoter } = usePromoterItemQuery();

  useCreateMissingOnboardingEvent(promoter);

  const onboardingEventId = promoter?.onboarding_event ?? undefined;
  const { data } = useIncompleteEventsItemQuery(onboardingEventId);
  const { mutate, isPending: mutateIsPending } = useIncompleteEventsItemUpdateMutation(onboardingEventId);

  // Keyed on the event itself, so the form data follows every refetch of it. The previous form
  // data stays visible while the new one is derived, so the steps do not blank out in between.
  const query = useQuery({
    enabled: !!data,
    queryFn: () => incompleteEventApiToIncompleteEventEditForm(data),
    queryKey: ['current-onboarding-form-data', data],
    placeholderData: keepPreviousData,
  });

  const mutation = useMutation({
    mutationFn: (updateData: DeepPartial<IncompleteEventFormComplete>) => {
      const apiData = incompleteEventEditFormToIncompleteEventApi(updateData);

      const refreshFormData = async () => {
        await tanstackQueryClient.invalidateQueries({
          queryKey: ['current-onboarding-form-data'],
        });

        config?.onMutationSuccess?.();
      };

      mutate(apiData, {
        onSuccess: () => {
          void refreshFormData();
        },
      });

      // Resolves right away, as before: completion is reported through onMutationSuccess.
      return Promise.resolve();
    },
  });

  return {
    mutation: { mutate: mutation.mutate, isPending: mutateIsPending },
    query,
  };
};
